import {z} from 'zod'

const SearchDateRangeTypeSchema = z.enum(['Specific', 'Flexible'])

const SearchDateRangeParamsSchema = z.object({
  type: SearchDateRangeTypeSchema,
  start: z.string().date().optional(),
  end: z.string().date().optional(),
  nights: z.number().optional()
})

export const SearchUnitGroupTypeSchema = z.enum([
  'BedRoom',
  'MeetingRoom',
  'EventSpace',
  'ParkingLot',
  'Other'
])

export const SearchSpecialRatesParamsSchema = z.object({
  promoCode: z.string().optional(),
  corporateCode: z.string().optional(),
  hasAAA: z.boolean(),
  hasAARP: z.boolean(),
  hasGov: z.boolean(),
  hasMilitary: z.boolean()
})

export const SearchParamsSchema = z.object({
  location: z.string(),
  dateRange: SearchDateRangeParamsSchema,
  guests: z.number(),
  propertyId: z.string().optional(),
  specialRates: SearchSpecialRatesParamsSchema
  // unitGroupTypes: z.array(SearchUnitGroupTypeSchema)
})

export const OfferRequestSchema = z.object({
  propertyId: z.string(),
  dateRange: SearchDateRangeParamsSchema,
  guests: z.number(),
  specialRates: SearchSpecialRatesParamsSchema.optional(),
  unitGroupTypes: z.array(SearchUnitGroupTypeSchema).optional()
})

const PropertySchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string()
})

export const UnitGroupSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  maxPersons: z.number(),
  type: z.string()
})

export const ServiceDateSchema = z.object({
  serviceDate: z.string(),
  amount: z.object({
    grossAmount: z.number(),
    netAmount: z.number(),
    vatType: z.string(),
    vatPercent: z.number(),
    currency: z.string()
  }),
  isDefaultDate: z.boolean(),
  isMandatory: z.boolean(),
  availableCount: z.number()
})

export const ServiceSchema = z.object({
  service: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    pricingUnit: z.string(),
    defaultGrossPrice: z.object({
      amount: z.number(),
      currency: z.string()
    })
  }),
  count: z.number(),
  availableCount: z.number(),
  totalAmount: z.object({
    grossAmount: z.number(),
    netAmount: z.number(),
    vatType: z.string(),
    vatPercent: z.number(),
    currency: z.string()
  }),
  prePaymentAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  dates: z.array(ServiceDateSchema).optional()
})

const FeeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  totalAmount: z.object({
    grossAmount: z.number(),
    netAmount: z.number(),
    vatType: z.string(),
    vatPercent: z.number(),
    currency: z.string()
  })
})

const TaxDetailSchema = z.object({
  vatType: z.string(),
  vatPercent: z.number(),
  net: z.object({amount: z.number(), currency: z.string()}),
  tax: z.object({amount: z.number(), currency: z.string()})
})

const CityTaxSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  totalGrossAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  dates: z.array(
    z.object({
      serviceDate: z.string(),
      amount: z.object({
        grossAmount: z.number(),
        netAmount: z.number(),
        vatType: z.string(),
        vatPercent: z.number(),
        currency: z.string()
      })
    })
  )
})

export const OfferSchema = z.object({
  arrival: z.string(),
  departure: z.string(),
  unitGroup: UnitGroupSchema,
  minGuaranteeType: z.string(),
  availableUnits: z.number(),
  ratePlan: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    isSubjectToCityTax: z.boolean()
  }),
  totalGrossAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  cancellationFee: z.object({
    code: z.string(),
    name: z.string(),
    description: z.string(),
    dueDateTime: z.string(),
    fee: z.object({amount: z.number(), currency: z.string()})
  }),
  noShowFee: z.object({
    code: z.string(),
    name: z.string(),
    description: z.string(),
    fee: z.object({amount: z.number(), currency: z.string()})
  }),
  timeSlices: z.array(
    z.object({
      from: z.string(),
      to: z.string(),
      availableUnits: z.number(),
      baseAmount: z.object({
        grossAmount: z.number(),
        netAmount: z.number(),
        vatType: z.string(),
        vatPercent: z.number(),
        currency: z.string()
      }),
      totalGrossAmount: z.object({
        amount: z.number(),
        currency: z.string()
      }),
      includedServices: z
        .array(
          z.object({
            service: z.object({
              id: z.string(),
              code: z.string(),
              name: z.string(),
              description: z.string()
            }),
            serviceDate: z.string(),
            count: z.number(),
            amount: z.object({
              grossAmount: z.number(),
              netAmount: z.number(),
              vatType: z.string(),
              vatPercent: z.number(),
              currency: z.string()
            }),
            pricingMode: z.string()
          })
        )
        .optional()
    })
  ),
  services: z.array(ServiceSchema),
  fees: z.array(FeeSchema),
  taxDetails: z.array(TaxDetailSchema).optional(),
  isCorporate: z.boolean(),
  prePaymentAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  cityTaxes: z.array(CityTaxSchema).optional(),
  validationMessages: z
    .array(
      z.object({
        code: z.string(),
        message: z.string()
      })
    )
    .optional(),
  promoCode: z.string().optional(),
  corporateCode: z.string().optional(),
  isMemberRate: z.boolean().optional()
})

export const OfferResultSchema = z.object({
  property: PropertySchema,
  offers: z.array(OfferSchema)
})

const MapCoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number()
})

export const ImageAssetSchema = z.object({
  image: z.string(),
  altText: z.string()
})

export const PropertyConfigSchema = z.object({
  propertyApaleoId: z.string(),
  name: z.string(),
  shortName: z.string(),
  phone: z.string(),
  description: z.string(),
  neighborhood: z.string(),
  location: z.string(),
  regions: z.array(z.string()),
  timeZone: z.string(),
  isEnabled: z.boolean(),
  isPopup: z.boolean(),
  images: z.array(ImageAssetSchema),
  features: z.array(z.string()),
  highlightedFeatures: z.array(z.string()),
  address: z.object({
    addressLine1: z.string(),
    addressLine2: z.string().optional(),
    postalCode: z.string(),
    city: z.string(),
    state: z.string(),
    countryCode: z.string()
  }),
  coordinates: MapCoordinatesSchema,
  openingDate: z.string().optional(),
  closingDate: z.string().optional()
})

export const UnitGroupConfigSchema = z.object({
  unitgroupApaleoId: z.string(),
  propertyApaleoId: z.string(),
  name: z.string(),
  description: z.string(),
  images: z.array(ImageAssetSchema),
  attributes: z.object({
    bedrooms: z.number(),
    bathrooms: z.number(),
    maxGuests: z.number(),
    unitSize: z.string().optional()
  }),
  highlightedFeatures: z.array(z.string()),
  basicFeatures: z.array(z.string()),
  sleepingFeatures: z.array(z.string()),
  diningFeatures: z.array(z.string()),
  workingFeatures: z.array(z.string()),
  familyFeatures: z.array(z.string()),
  outdoorFeatures: z.array(z.string()),
  safetyFeatures: z.array(z.string()),
  accessibilityFeatures: z.array(z.string())
})

export const IbeConfigSchema = z.object({
  locations: z.array(z.string()),
  properties: z.array(PropertyConfigSchema),
  regions: z.array(z.string()),
  unitGroups: z.array(UnitGroupConfigSchema)
})

export const UnitGroupOfferSchema = z.object({
  propertyId: z.string(),
  unitGroup: UnitGroupSchema,
  availableUnits: z.number(),
  lowestGrossAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  lowestNightlyAmount: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  nights: z.number(),
  offers: z.array(OfferSchema)
})

export const ToastNotificationSchema = z.object({
  id: z.string(),
  message: z.string(),
  type: z.enum(['add', 'remove'])
})

export const ServiceOfferRequestSchema = z.object({
  ratePlanId: z.string(),
  dateRange: SearchDateRangeParamsSchema,
  guests: z.number()
})

export const ServiceOfferSchema = z.object({
  service: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    pricingUnit: z.enum(['Person', 'Room']),
    defaultGrossPrice: z.object({
      amount: z.number(),
      currency: z.string()
    })
  }),
  count: z.number(),
  totalAmount: z.object({
    grossAmount: z.number(),
    netAmount: z.number(),
    vatType: z.string(),
    vatPercent: z.number(),
    currency: z.string()
  }),
  prePaymentAmount: z.object({amount: z.number(), currency: z.string()}),
  fees: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      totalAmount: z.object({
        grossAmount: z.number(),
        netAmount: z.number(),
        vatType: z.string(),
        vatPercent: z.number(),
        currency: z.string()
      })
    })
  ),
  dates: z.array(
    z.object({
      serviceDate: z.string(),
      amount: z.object({
        grossAmount: z.number(),
        netAmount: z.number(),
        vatType: z.string(),
        vatPercent: z.number(),
        currency: z.string()
      }),
      isDefaultDate: z.boolean(),
      isMandatory: z.boolean()
    })
  )
})

export const ShoppingCartItemSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  offer: OfferSchema,
  additionalServices: z.array(ServiceOfferSchema),
  guests: z.number()
})

export const ItemEditorModeSchema = z.enum(['Add', 'Edit'])

export const ItemEditSchema = z.object({
  item: ShoppingCartItemSchema,
  mode: ItemEditorModeSchema
})

export const ShoppingCartValidationErrorSchema = z.object({
  id: z.string(),
  code: z.enum([
    'not_available',
    'not_enough_available',
    'price_increase',
    'price_decrease',
    'invalid_arrival_date'
  ]),
  message: z.string()
})

export const ShoppingCartValidationResultSchema = z.object({
  latestOffers: z.array(OfferResultSchema),
  validationErrors: z.array(ShoppingCartValidationErrorSchema),
  lastValidated: z.union([z.string().datetime(), z.null()]).optional()
})

export const ShoppingCartValidationSchema = z.object({
  isCartValidationOpen: z.boolean(),
  latestOffers: z.array(OfferResultSchema),
  validationErrors: z.array(ShoppingCartValidationErrorSchema),
  lastValidated: z.union([z.string().datetime(), z.null()]).optional()
})

export const ShoppingCartSchema = z.object({
  items: z.array(ShoppingCartItemSchema),
  isCartOpen: z.boolean(),
  isItemEditorOpen: z.boolean(),
  itemEditorMode: ItemEditorModeSchema,
  itemToEdit: z.union([ShoppingCartItemSchema, z.null()]).optional()
})

const BookingGuestSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string()
})

export const BookingReservationSchema = z.object({
  arrival: z.string(),
  departure: z.string(),
  adults: z.number(),
  childrenAges: z.array(z.number()),
  guestComment: z.string().optional(),
  channelCode: z.literal('Ibe'),
  primaryGuest: BookingGuestSchema,
  guaranteeType: z.string(),
  timeSlices: z.array(z.object({ratePlanId: z.string()})),
  services: z.array(z.object({serviceId: z.string()})),
  prePaymentAmount: z.object({amount: z.number(), currency: z.string()}),
  promoCode: z.union([z.string(), z.null()]).optional(),
  corporateCode: z.union([z.string(), z.null()]).optional()
})

export const CreateIbeBookingMetadataSchema = z.object({
  type: z.literal('CreateIbeBooking'),
  ibeSessionId: z.string(),
  booker: BookingGuestSchema,
  reservations: z.array(BookingReservationSchema),
  totalPrepayment: z.object({
    amount: z.number(),
    currency: z.string()
  }),
  total: z.object({
    currency: z.string(),
    amount: z.number()
  })
})

export const PaymentSessionRequestSchema = z.object({
  amountInCents: z.number(),
  isPaymentAccount: z.boolean(),
  metadata: CreateIbeBookingMetadataSchema,
  propertyId: z.string(),
  returnUrl: z.string(),
  shopperId: z.string().optional(),
  shopperEmail: z.string(),
  lastUpdated: z.union([z.string(), z.null()]).optional()
})

export const PaymentSessionSchema = z.object({
  id: z.string(),
  paymentSessionId: z.string(),
  sessionData: z.string()
})

export const HubspotPropertySchema = z.object({
  id: z.number().optional(),
  property_id: z.string().optional(),
  pm_property_name: z.string().optional(),
  city_page_url_slug: z.string().optional(),
  location_group: z.string().optional()
})

export const HubspotPropertyResultsSchema = z.object({
  hasMore: z.boolean(),
  offset: z.number(),
  results: z.array(HubspotPropertySchema),
  total: z.number()
})

export const HubspotLocationSchema = z.object({
  name: z.string(),
  url: z.string()
})

export const OfferChargeSchema = z.object({
  name: z.string(),
  amount: z.number()
})

export const DatedOfferChargeSchema = OfferChargeSchema.extend({
  date: z.string()
})

export const OfferPriceSummarySchema = z.object({
  itemizedRoomCharges: z.array(DatedOfferChargeSchema),
  totalRoomCharges: z.number(),
  itemizedIncludedServices: z.array(DatedOfferChargeSchema),
  itemizedAdditionalServices: z.array(DatedOfferChargeSchema),
  totalIncludedServices: z.number(),
  totalAdditionalServices: z.number(),
  itemizedTaxesAndFees: z.array(OfferChargeSchema),
  totalTaxesAndFees: z.number(),
  totalPrepayment: z.number(),
  total: z.number()
})

export const ShoppingCartPriceSummarySchema = z.object({
  totalRoomCharges: z.number(),
  totalIncludedServices: z.number(),
  totalAdditionalServices: z.number(),
  totalTaxesAndFees: z.number(),
  totalPrepayment: z.number(),
  balance: z.number(),
  total: z.number()
})

export const UserSchema = z.object({
  isAuthenticated: z.boolean(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional()
})

export const GuestInfoSchema = z.object({
  firstName: z.string().min(1, 'First Name is required'),
  lastName: z.string().min(1, 'Last Name is required'),
  phone: z.string().min(7, 'Mobile Phone is required'),
  formattedPhone: z.string().optional(),
  email: z.string().min(1, 'Email is required').email(),
  comments: z.string().optional(),
  acceptTerms: z.boolean().refine(value => value === true),
  lastUpdated: z.union([z.string(), z.null()]).optional()
})

export const SpecialRatePlansSchema = z.object({
  name: z.string(),
  code: z.string(),
  ratePlanIds: z.array(z.string()),
  isCorp: z.boolean().optional(),
  isCorpAR: z.boolean().optional()
})

export const RatePlanOfferRequestSchema = z.object({
  ratePlanId: z.string(),
  arrival: z.string().date(),
  departure: z.string().date(),
  guests: z.number()
})

export const LatestOffersRequestSchema = z.object({
  requests: z.array(RatePlanOfferRequestSchema),
  lastUpdated: z.string()
})

export const PaymentStatusSchema = z.enum([
  'Inactive',
  'Processing',
  'Success',
  'Failure'
])

export const PaymentInfoSchema = z.object({
  paymentSessionId: z.string().nullable().optional(),
  status: PaymentStatusSchema,
  error: z.string().optional()
})

export const BookingStatusSchema = z.enum([
  'Inactive',
  'Processing',
  'Success',
  'Failure'
])

export const BookingInfoSchema = z.object({
  bookingId: z.string().nullable().optional(),
  status: BookingStatusSchema
})
